/**
 * FileUploadR, the file upload field.
 * Handles type: file_upload.
 *
 * The registered value is NOT the raw <input type="file"> FileList, since
 * that can't survive a JSON round-trip through intake_field_data (a File
 * object has no own enumerable properties, so it used to serialise as junk
 * like {"0":{}}). The registered value is UPLOAD METADATA (document_id,
 * storage_path, filename, size_bytes, mime_type, uploaded_at), written via
 * setValue() once uploadDocument() succeeds, so the same debounced autosave
 * that persists every other answer persists this one too.
 *
 * Where the file actually goes (storage path, documents row, version chain,
 * drive sync) is entirely owned by uploadDocument() in src/lib/uploadDocument.ts;
 * this component only supplies the who/where (matterId/contactId/uploaderRole,
 * read from UploadTarget context) and the what (the picked File). See
 * src/lib/fileUploadValue.ts for the value shape + isUploadedFileValue guard.
 *
 * In PDF mode renders the filename if available.
 */

import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import type { FieldDef, RendererProps } from '../types';
import { resolveLabel, resolveHelpText, deriveFieldName } from '../utils';
import { uploadDocument, canonicalNameToChecklistKey, UploadValidationError } from '../../uploadDocument';
import { isUploadedFileValue, formatFileSize, type UploadedFileValue } from '../../fileUploadValue';
import { useUploadTarget } from '../UploadTarget';

interface Props extends RendererProps {
  field: FieldDef;
}

// Mirrors MAX_FILE_BYTES in src/pages/Documents.tsx (the per-checklist-item
// upload surface), kept in sync by hand since that constant isn't exported.
// Server-side bucket cap is 50 MB; both UI surfaces reject earlier for a
// gentler error message. Image megapixel downscaling happens inside
// uploadDocument() regardless of this cap.
const MAX_FILE_BYTES = 25 * 1024 * 1024;

// Aligned with uploadDocument's ALLOWED_MIME/ALLOWED_EXT allowlist (PDF, JPG,
// PNG, HEIC/HEIF). HEIC/HEIF have no MIME type browsers reliably report, so
// the extensions are listed alongside image/*, the same accept string
// Documents.tsx already ships.
const ACCEPT = 'image/*,application/pdf,.heic,.heif';

type LocalStatus = 'idle' | 'uploading' | 'error';

export function FileUploadR({ field, formId, pdfMode = false, namePrefix, audience }: Props) {
  const { register, getValues, watch, setValue } = useFormContext();
  const uploadTarget = useUploadTarget();
  const [status, setStatus] = useState<LocalStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const name = deriveFieldName(field, namePrefix);
  const label = resolveLabel(field, formId, audience);
  const value = watch(name);

  if (pdfMode) {
    const pdfValue = getValues(name);
    const filename =
      typeof pdfValue === 'string'
        ? pdfValue
        : (pdfValue as { filename?: string } | null)?.filename ??
          (pdfValue as { name?: string } | null)?.name;
    return (
      <div>
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        <p className="text-sm text-slate-800 mt-0.5">
          {filename
            ? <span>[Attached: {filename}]</span>
            : <span className="text-slate-400 italic">Not provided</span>
          }
        </p>
      </div>
    );
  }

  // Register the field so react-hook-form tracks it for validation, dirty
  // state, and the debounced autosave watch, even though no native input
  // ever carries this value directly (it's written via setValue() below).
  // A raw FileList or legacy junk value (object without document_id) must
  // NOT satisfy required, hence the custom validate rather than `required`.
  register(name, {
    validate: field.required
      ? (v: unknown) => isUploadedFileValue(v) || `${label} is required`
      : undefined,
  });

  const hint = resolveHelpText(field, audience) ?? 'PDF, JPG, PNG, or HEIC/HEIF, max 25 MB';

  if (!uploadTarget) {
    return (
      <div>
        <label className="form-label">
          {label} {field.required && <span className="text-[#FB8E28]">*</span>}
        </label>
        <input
          type="file"
          disabled
          className="block w-full text-sm text-slate-400 border border-slate-200 rounded-lg p-1.5 bg-slate-50 cursor-not-allowed"
        />
        <p className="form-hint">File uploads are available inside your intake form.</p>
      </div>
    );
  }

  const uploaded = isUploadedFileValue(value) ? value : null;

  async function handleFiles(file: File | null) {
    if (!file || !uploadTarget) return;

    if (file.size > MAX_FILE_BYTES) {
      setStatus('error');
      setError(
        `${file.name} is ${formatFileSize(file.size)}, larger than the ${formatFileSize(MAX_FILE_BYTES)} per-file cap. Split or compress and try again.`,
      );
      return;
    }

    setStatus('uploading');
    setError(null);

    try {
      const result = await uploadDocument({
        file,
        matterId: uploadTarget.matterId,
        contactId: uploadTarget.contactId,
        uploaderRole: uploadTarget.uploaderRole,
        checklistItem: canonicalNameToChecklistKey(name),
        replacesDocumentId: uploaded?.document_id,
      });

      const meta: UploadedFileValue = {
        document_id: result.documentId,
        storage_path: result.storagePath,
        filename: result.filename,
        size_bytes: result.sizeBytes,
        mime_type: result.mimeType,
        uploaded_at: new Date().toISOString(),
      };
      setValue(name, meta, { shouldDirty: true, shouldValidate: true });
      setStatus('idle');
    } catch (err) {
      setStatus('error');
      setError(
        err instanceof UploadValidationError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Upload failed. Please try again.',
      );
    }
  }

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    // e.target.files is a LIVE FileList: resetting input value empties it, so
    // the File must be plucked out synchronously BEFORE the reset. The reset
    // lets the same file be re-picked after an error.
    const file = e.target.files?.[0] ?? null;
    e.target.value = '';
    void handleFiles(file);
  }

  return (
    <div>
      <label className="form-label">
        {label} {field.required && <span className="text-[#FB8E28]">*</span>}
      </label>

      {status === 'uploading' ? (
        <input
          type="file"
          disabled
          accept={ACCEPT}
          className="block w-full text-sm text-slate-400 border border-slate-200 rounded-lg p-1.5 bg-slate-50 cursor-not-allowed"
        />
      ) : uploaded ? (
        <div className="flex items-center justify-between gap-3 border border-slate-300 rounded-lg p-2 bg-slate-50">
          <div className="flex items-center gap-2 min-w-0">
            <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
            <span className="text-sm text-slate-800 truncate">{uploaded.filename}</span>
            <span className="text-xs text-slate-400 shrink-0">{formatFileSize(uploaded.size_bytes)}</span>
          </div>
          <label className="text-xs font-semibold text-[#0B2341] bg-[#EBF7FD] hover:bg-[#d4eefa] rounded px-2 py-1 cursor-pointer shrink-0">
            Replace
            <input type="file" accept={ACCEPT} className="sr-only" onChange={onChange} />
          </label>
        </div>
      ) : (
        <input
          type="file"
          accept={ACCEPT}
          onChange={onChange}
          className="block w-full text-sm text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-[#EBF7FD] file:text-[#0B2341] hover:file:bg-[#d4eefa] cursor-pointer border border-slate-300 rounded-lg p-1"
        />
      )}

      {status === 'uploading' && (
        <p className="flex items-center gap-1.5 text-xs text-slate-500 mt-1">
          <Loader2 size={13} className="animate-spin" />
          Uploading...
        </p>
      )}

      {status === 'error' && error && (
        <p className="form-error flex items-center gap-1 mt-1" role="alert">
          <AlertCircle size={13} className="shrink-0" />
          {error}
        </p>
      )}

      <p className="form-hint">{hint}</p>
    </div>
  );
}
